import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { User, Building2 } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import {
  usePlans,
  useCurrentSubscription,
  useCreateCheckout,
  useChangePlan,
  useCancelSubscription,
  useRevertCancellation,
  useCancelScheduledChange,
} from '../features/subscriptions';
import type { SubscriptionPlan } from '../features/subscriptions';
import { useUser } from '../features/auth/useUser';
import { useGenerationModels } from '../features/generation-models/hooks';
import { hardNavigate } from '../utils/navigation';
import { ExpiryWarningBanner } from '../components/credits/ExpiryWarningBanner';
import { MANAGED_PLAN_PURIFY_CONFIG } from './org-admin/OrgPlansPage';
import './SubscriptionPage.css';

export default function SubscriptionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useUser();
  const toast = useToast();
  const plansQuery = usePlans();
  const subscriptionQuery = useCurrentSubscription();
  // 料金表の Seedance 2.5 行は「このユーザーにモデルが見えているか」で出し分ける。
  // generation-models API はパートナー別 feature_flags 適用済みなので、SAITE (hypeapp)
  // など未開放パートナーではブランド判定をハードコードせずに自然と非表示になる。
  const videoModelsQuery = useGenerationModels('video');
  const showSeedance25 = (videoModelsQuery.data ?? []).some(
    (m) => m.model_code === 'seedance-25-standard',
  );
  const showMinimaxH3 = (videoModelsQuery.data ?? []).some(
    (m) => m.model_code === 'minimax-h3',
  );

  const createCheckout = useCreateCheckout();
  const changePlanMutation = useChangePlan();
  const cancelMutation = useCancelSubscription();
  const revertMutation = useRevertCancellation();
  const cancelScheduledChangeMutation = useCancelScheduledChange();

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<'personal' | 'business'>('personal');
  // 初回ロード時に「現在のプラン」が属するタブを既定表示にする (ユーザー操作後は上書きしない)
  const didInitCategory = useRef(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // モーダル表示中は背面のページスクロールを止める。
  // (スクロールしたままだとオーバーレイと背面の位置がずれて見える)
  const isModalOpen = showCancelModal || showDowngradeModal || showUpgradeModal;
  useEffect(() => {
    if (!isModalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isModalOpen]);

  // URLパラメータでの成功/キャンセル処理
  useEffect(() => {
    if (searchParams.get('success') === 'true' && plansQuery.data?.plans) {
      const planCode = searchParams.get('plan');
      const planName = plansQuery.data.plans.find(p => p.plan_code === planCode)?.display_name;
      toast.success(`${planName ? planName + 'への' : ''}契約が完了しました`);
      setSearchParams({});
      // データを再取得
      plansQuery.refetch();
      subscriptionQuery.refetch();
    } else if (searchParams.get('canceled') === 'true') {
      toast.show('契約手続きがキャンセルされました');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, plansQuery.data?.plans, subscriptionQuery]);

  // 初回ロード時、現在のプランが属するタブ (個人向け/法人向け) を既定で開く。
  // 'both' (お任せ等) のプランはどちらにも属さないため既定 (personal) のまま。
  useEffect(() => {
    if (didInitCategory.current) return;
    const loadedPlans = plansQuery.data?.plans;
    if (!loadedPlans?.length) return;
    const current = loadedPlans.find((p) => p.is_current);
    if (current?.plan_category === 'personal' || current?.plan_category === 'business') {
      setActiveCategory(current.plan_category);
    }
    didInitCategory.current = true;
  }, [plansQuery.data?.plans]);

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (!isAdmin) {
      toast.error('管理者のみプランを変更できます');
      return;
    }

    if (plan.is_current) {
      return;
    }

    if (plan.is_contact_sales) {
      if (plan.contact_sales_url) {
        window.open(plan.contact_sales_url, '_blank');
      } else {
        toast.error('お問い合わせ先が設定されていません');
      }
      return;
    }

    const currentPlan = plansQuery.data?.current_plan || 'free';
    // sort_order順にソートされたplansからplan_codeの配列を生成
    const planOrder = plansQuery.data?.plans?.map(p => p.plan_code) || [];
    const currentIndex = planOrder.indexOf(currentPlan);
    const newIndex = planOrder.indexOf(plan.plan_code);

    if (plan.plan_code === 'free') {
      // Freeへのダウングレード
      setShowCancelModal(true);
      return;
    }

    if (newIndex < currentIndex) {
      // ダウングレード
      setSelectedPlan(plan.plan_code);
      setShowDowngradeModal(true);
      return;
    }

    // アップグレードまたは新規契約
    const currentPaidSub =
      subscriptionQuery.data?.plan.plan_code &&
      subscriptionQuery.data.plan.plan_code !== 'free';

    if (currentPaidSub) {
      // 既存有料サブスクからのアップグレード: 即時課金されるため確認モーダルを挟む
      setSelectedPlan(plan.plan_code);
      setShowUpgradeModal(true);
      return;
    }

    // 新規契約 (Free → 有料): Stripe Checkout で決済確認されるためそのまま遷移
    try {
      const result = await createCheckout.mutateAsync(plan.plan_code);

      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else if (result.payment_required) {
        // 按分請求が未払い / 3DS 待ち。決済を完了するまでプランは切り替わらない
        if (result.payment_url) {
          window.location.href = result.payment_url;
        } else {
          toast.error(result.message || 'お支払いの確認が必要です');
        }
      } else if (result.upgraded) {
        toast.success(result.message || 'アップグレードが完了しました');
        plansQuery.refetch();
        subscriptionQuery.refetch();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'エラーが発生しました');
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;

    try {
      const result = await createCheckout.mutateAsync(selectedPlan);

      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else if (result.payment_required) {
        // 按分請求が未払い / 3DS 待ち。決済を完了するまでプランは切り替わらない
        if (result.payment_url) {
          window.location.href = result.payment_url;
        } else {
          toast.error(result.message || 'お支払いの確認が必要です');
          setShowUpgradeModal(false);
          setSelectedPlan(null);
        }
      } else if (result.upgraded) {
        toast.success(result.message || 'アップグレードが完了しました');
        setShowUpgradeModal(false);
        setSelectedPlan(null);
        plansQuery.refetch();
        subscriptionQuery.refetch();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'エラーが発生しました');
    }
  };

  const handleDowngrade = async () => {
    if (!selectedPlan) return;

    try {
      const result = await changePlanMutation.mutateAsync(selectedPlan);
      toast.success(result.message);
      setShowDowngradeModal(false);
      setSelectedPlan(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'プラン変更に失敗しました');
    }
  };

  const handleCancel = async () => {
    try {
      const result = await cancelMutation.mutateAsync();
      toast.success(result.message);
      setShowCancelModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'ダウングレードに失敗しました');
    }
  };

  const handleRevertCancel = async () => {
    try {
      const result = await revertMutation.mutateAsync();
      toast.success(result.message);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'ダウングレード取り消しに失敗しました');
    }
  };

  const handleCancelScheduledChange = async () => {
    try {
      const result = await cancelScheduledChangeMutation.mutateAsync();
      toast.success(result.message);
    } catch (error: any) {
      toast.error(error.response?.data?.error || '変更予約の取り消しに失敗しました');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (plansQuery.isLoading || subscriptionQuery.isLoading) {
    return (
      <div className="page admin-page subscription-page">
        <div className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">
            <img src="/money_wallet_open.svg" alt="" className="page-title-icon" />
            プラン・契約
          </h1>
          </div>
        </div>
        <div className="admin-loading">読み込み中...</div>
      </div>
    );
  }

  const plans = plansQuery.data?.plans || [];
  // 個人向け/法人向けタブ表示 (パートナー単位)。false (商品上品=default 等) は 1枚表示。
  const showCategoryTabs = plansQuery.data?.show_plan_category_tabs ?? false;
  // タブ表示時: アクティブなタブのプラン + どちらのタブでも表示する 'both' (お任せ等)
  const visiblePlans = showCategoryTabs
    ? plans.filter((p) => p.plan_category === 'both' || p.plan_category === activeCategory)
    : plans;
  const subscription = subscriptionQuery.data;
  const isCanceling = subscription?.status === 'canceling' || subscription?.cancel_at_period_end;
  // 同一プランへの no-op schedule はバックエンドで除外済みだが、二重防御として
  // フロント側でも new_plan === 現プランのケースは表示しない。
  // 詳細: docs/requirements/subscription-noop-schedule-fix-plan.md §5.2 変更 4
  const hasScheduledChange =
    subscription?.scheduled_change != null &&
    subscription?.scheduled_change?.new_plan !== subscription?.plan?.plan_code;
  const scheduledPlan = hasScheduledChange
    ? plans.find((p) => p.plan_code === subscription?.scheduled_change?.new_plan)
    : null;

  // プロジェクト数上限 / ミーティング枠は、当該パートナーのプラン群で
  // 該当機能が使われている場合のみ比較行を出す（partner をハードコードせず DB 駆動）。
  const anyProductLimit = plans.some((p) => p.max_products != null);
  const anyMtg = plans.some((p) => (p.mtg_credits_per_session ?? 0) > 0);

  const formatMtgQuota = (plan: SubscriptionPlan): string => {
    const per = plan.mtg_credits_per_session ?? 0;
    if (per <= 0) return 'なし';
    const free = plan.mtg_free_sessions_per_month ?? 0;
    return free > 0
      ? `月${free}回無料＋追加${per.toLocaleString()}pt/回`
      : `${per.toLocaleString()}pt/回`;
  };

  // Checkout 実行中の対象プラン。isPending だけで判定すると全プランのボタンがローディングになる。
  const checkoutPendingPlan = createCheckout.isPending ? createCheckout.variables : null;

  const renderPlanCard = (plan: SubscriptionPlan) => (
    <div
      key={plan.plan_code}
      className={`subscription-plan-card ${plan.is_current ? 'is-current' : ''} plan-${plan.plan_code}`}
    >
      {plan.is_current && (
        <div className="subscription-plan-current-badge">現在のプラン</div>
      )}
      <div className="subscription-plan-header">
        <h3 className="subscription-plan-name">{plan.display_name}</h3>
        <div className="subscription-plan-price">
          {plan.is_contact_sales && plan.monthly_fee_with_tax === 0 ? (
            <span className="price-free">—</span>
          ) : plan.monthly_fee_with_tax === 0 ? (
            <span className="price-free">無料</span>
          ) : (
            <>
              <span className="price-amount">
                ¥{plan.monthly_fee_with_tax.toLocaleString()}{plan.price_from ? '~' : ''}
              </span>
              <span className="price-period">/月</span>
            </>
          )}
        </div>
      </div>
      {plan.plan_code !== 'managed' && (
        <div className="subscription-plan-credits">
          <span className="credits-label-prefix">期限付きポイント</span>
          {plan.included_credits > 0 ? (
            <>
              <div className="credits-row">
                <span className="credits-amount">{plan.included_credits.toLocaleString()}{plan.price_from ? '~' : ''}</span>
                <span className="credits-unit">pt</span>
              </div>
              <span className="credits-label">毎月付与</span>
            </>
          ) : (
            <>
              <div className="credits-row">
                <span className="credits-none">—</span>
              </div>
              <span className="credits-label">付与なし</span>
            </>
          )}
        </div>
      )}
      {plan.managed_plan_html ? (
        <div
          className="subscription-plan-html"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(plan.managed_plan_html, MANAGED_PLAN_PURIFY_CONFIG),
          }}
        />
      ) : (
        <ul className="subscription-plan-features">
          {plan.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
          {anyProductLimit && (
            <li>
              プロジェクト数：{plan.max_products == null ? '無制限' : `最大${plan.max_products.toLocaleString()}件`}
            </li>
          )}
          {anyMtg && (
            <li>ミーティング枠：{formatMtgQuota(plan)}</li>
          )}
          {plan.ads_api_unlimited && (
            <li className="feature-highlight">広告API無償化</li>
          )}
        </ul>
      )}
      <div className="subscription-plan-action">
        {plan.is_current ? (
          <>
            <button className="btn-plan btn-current" disabled>
              現在のプラン
            </button>
            {(plan.mtg_credits_per_session ?? 0) > 0 && (
              <button
                type="button"
                className="subscription-plan-meeting-link"
                onClick={() => hardNavigate('/support-meeting')}
              >
                ミーティングを設定
              </button>
            )}
          </>
        ) : plan.is_contact_sales ? (
          <button
            className="btn-plan btn-contact"
            onClick={() => handleSelectPlan(plan)}
          >
            お問い合わせ
          </button>
        ) : (
          <button
            className="btn-plan btn-select"
            onClick={() => handleSelectPlan(plan)}
            disabled={!isAdmin || createCheckout.isPending}
          >
            {checkoutPendingPlan === plan.plan_code ? '処理中...' : '選択'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="page admin-page subscription-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/money_wallet_open.svg" alt="" className="page-title-icon" />
            プラン・契約
          </h1>
          <p className="page-description">プラン選択・料金表</p>
        </div>
      </div>

      {/* 失効警告バナー */}
      <ExpiryWarningBanner />

      {/* 現在の契約情報 */}
      {subscription && subscription.plan.plan_code !== 'free' && (
        <div className="subscription-current-card">
          <div className="subscription-current-content">
            <div className="subscription-current-layout">
              <div className="subscription-current-left">
                <div className="subscription-current-header">
                  <h2>現在のプラン</h2>
                  {isCanceling && (
                    <span className="subscription-badge subscription-badge-warning">ダウングレード予約済み</span>
                  )}
                  {hasScheduledChange && !isCanceling && (
                    <span className="subscription-badge subscription-badge-info">変更予約済み</span>
                  )}
                </div>
                <div className="subscription-current-plan">
                  <span className="subscription-plan-name">{subscription.plan.display_name}</span>
                  <span className="subscription-plan-price">
                    ¥{subscription.plan.monthly_fee_with_tax.toLocaleString()}/月（税込）
                  </span>
                </div>
                <div className="subscription-current-credits">
                  <span className="subscription-credits-label">月間付与ポイント</span>
                  <span className="subscription-credits-value">
                    {subscription.plan.included_credits.toLocaleString()}
                    <span className="subscription-credits-unit">pt</span>
                    <span className="subscription-credits-note">（期限付き）</span>
                  </span>
                </div>
              </div>
              <div className="subscription-current-right">
                <div className="subscription-detail-item">
                  <span className="subscription-detail-label">契約開始日</span>
                  <span className="subscription-detail-value">
                    {formatDate(subscription.current_period_start)}
                  </span>
                </div>
                <div className="subscription-detail-item">
                  <span className="subscription-detail-label">
                    {isCanceling ? '契約終了日' : '次回更新日'}
                  </span>
                  <span className="subscription-detail-value">
                    {formatDate(subscription.current_period_end)}
                    {hasScheduledChange && scheduledPlan && (
                      <span className="subscription-next-plan">
                        → {scheduledPlan.display_name}
                      </span>
                    )}
                  </span>
                  {hasScheduledChange && !isCanceling && (
                    <button
                      className="btn-modal-success-sm subscription-cancel-change-btn"
                      onClick={handleCancelScheduledChange}
                      disabled={cancelScheduledChangeMutation.isPending}
                    >
                      {cancelScheduledChangeMutation.isPending ? '処理中...' : '変更予約をキャンセル'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            {isCanceling ? (
              <div className="subscription-cancel-notice">
                <p>
                  {formatDate(subscription.current_period_end)}にフリープランへダウングレードされます。
                  ダウングレード後も期限付きポイントは有効期限まで使用可能です。
                </p>
                <button
                  className="btn-modal-success"
                  onClick={handleRevertCancel}
                  disabled={revertMutation.isPending}
                >
                  {revertMutation.isPending ? '処理中...' : 'ダウングレードを取り消す'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* プラン選択 */}
      <div className="subscription-plans-section">
        {showCategoryTabs ? (
          <div className={`subscription-category-folder cat-${activeCategory}`}>
            <div className="subscription-folder-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === 'personal'}
                className={`subscription-folder-tab ${activeCategory === 'personal' ? 'is-active' : ''}`}
                onClick={() => setActiveCategory('personal')}
              >
                <div className="tab-icon-wrapper">
                  <User size={22} />
                </div>
                <div className="tab-text-wrapper">
                  <span className="tab-title">個人向け</span>
                  <span className="tab-subtitle">1名でご利用の場合</span>
                </div>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === 'business'}
                className={`subscription-folder-tab ${activeCategory === 'business' ? 'is-active' : ''}`}
                onClick={() => setActiveCategory('business')}
              >
                <div className="tab-icon-wrapper">
                  <Building2 size={22} />
                </div>
                <div className="tab-text-wrapper">
                  <span className="tab-title">法人向け</span>
                  <span className="tab-subtitle">チーム・組織でご利用の場合</span>
                </div>
              </button>
            </div>
            
            <div className="subscription-folder-content">
              <div className="subscription-plans-grid">
                {visiblePlans.map(renderPlanCard)}
              </div>
            </div>
          </div>
        ) : (
          <div className="subscription-plans-grid">
            {visiblePlans.map(renderPlanCard)}
          </div>
        )}
        <p className="subscription-section-note">
          ※ 期限付きポイントは毎月の更新日に付与され、次の更新日に失効します
          <br />
          ※ 金額は税込です
        </p>
      </div>

      {/* ポイント料金表 */}
      <div className="subscription-pricing-section">
        <h2 className="subscription-section-title">モデル別ポイント料金表</h2>
        <div className="subscription-pricing-table-wrapper">
          <table className="subscription-pricing-table">
            <thead>
              <tr>
                <th>種別</th>
                <th>AIプロバイダ</th>
                <th>モード</th>
                <th className="th-res">4K</th>
                <th className="th-res">2K</th>
                <th className="th-res">1K</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowSpan={12}>画像生成<br /><span className="model-name">／枚</span></td>
                <td rowSpan={5}>OpenAI GPT 標準</td>
                <td>最高品質<br /><span className="model-name">gpt-image-2.5-flare-max</span></td>
                <td className="price">3,000 pt</td>
                <td className="price">800 pt</td>
                <td className="price">200 pt</td>
              </tr>
              <tr>
                <td>超高品質<br /><span className="model-name">gpt-image-2.5-flare-xhigh</span></td>
                <td className="price">1,800 pt</td>
                <td className="price">480 pt</td>
                <td className="price">120 pt</td>
              </tr>
              <tr>
                <td>高品質<br /><span className="model-name">gpt-image-2.5-flare-high</span></td>
                <td className="price">1,200 pt</td>
                <td className="price">320 pt</td>
                <td className="price">80 pt</td>
              </tr>
              <tr>
                <td>標準／自動<br /><span className="model-name">gpt-image-2.5-flare-medium / auto</span></td>
                <td className="price">750 pt</td>
                <td className="price">200 pt</td>
                <td className="price">50 pt</td>
              </tr>
              <tr>
                <td>低品質<br /><span className="model-name">gpt-image-2.5-flare-low</span></td>
                <td className="price">150 pt</td>
                <td className="price">40 pt</td>
                <td className="price">10 pt</td>
              </tr>
              <tr>
                <td rowSpan={4}>OpenAI GPT 長時間</td>
                <td>最高品質<br /><span className="model-name">gpt-image-2.5-sunburst-max</span></td>
                <td className="price">3,000 pt</td>
                <td className="price">800 pt</td>
                <td className="price">200 pt</td>
              </tr>
              <tr>
                <td>超高品質<br /><span className="model-name">gpt-image-2.5-sunburst-xhigh</span></td>
                <td className="price">1,800 pt</td>
                <td className="price">480 pt</td>
                <td className="price">120 pt</td>
              </tr>
              <tr>
                <td>高品質<br /><span className="model-name">gpt-image-2.5-sunburst-high</span></td>
                <td className="price">1,200 pt</td>
                <td className="price">320 pt</td>
                <td className="price">80 pt</td>
              </tr>
              <tr>
                <td>自動<br /><span className="model-name">gpt-image-2.5-sunburst-auto</span></td>
                <td className="price">750 pt</td>
                <td className="price">200 pt</td>
                <td className="price">50 pt</td>
              </tr>
              <tr>
                <td rowSpan={3}>Google Gemini<br />Nano-Banana</td>
                <td>高品質<br /><span className="model-name">gemini-3-pro-image</span></td>
                <td className="price">80 pt</td>
                <td className="price">50 pt</td>
                <td className="price">50 pt</td>
              </tr>
              <tr>
                <td>標準<br /><span className="model-name">gemini-nano-banana-2.1</span></td>
                <td className="price">30 pt</td>
                <td className="price">20 pt</td>
                <td className="price">15 pt</td>
              </tr>
              <tr>
                <td>スピード<br /><span className="model-name">gemini-3.1-flash-lite-image</span></td>
                <td className="na">-</td>
                <td className="na">-</td>
                <td className="price">10 pt</td>
              </tr>
            </tbody>
          </table>
          <table className="subscription-pricing-table">
            <thead>
              <tr>
                <th>種別</th>
                <th>AIプロバイダ</th>
                <th>モード</th>
                <th className="th-res">4K</th>
                <th className="th-res">1080p</th>
                <th className="th-res">720p</th>
                <th className="th-res">480p</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowSpan={(showSeedance25 ? 4 : 3) + 1 + (showMinimaxH3 ? 1 : 0)}>動画生成<br /><span className="model-name">／秒</span></td>
                <td rowSpan={showSeedance25 ? 4 : 3}>ByteDance Seedance</td>
                {showSeedance25 ? (
                  <>
                    <td>2.5 標準<br /><span className="model-name">seedance-25-standard</span></td>
                    <td className="na">-</td>
                    <td className="price">285 pt</td>
                    <td className="price">115 pt</td>
                    <td className="price">55 pt</td>
                  </>
                ) : (
                  <>
                    <td>2.0 標準<br /><span className="model-name">seedance-2-standard</span></td>
                    <td className="price">375 pt</td>
                    <td className="price">175 pt</td>
                    <td className="price">75 pt</td>
                    <td className="price">35 pt</td>
                  </>
                )}
              </tr>
              {showSeedance25 && (
                <tr>
                  <td>2.0 標準<br /><span className="model-name">seedance-2-standard</span></td>
                  <td className="price">375 pt</td>
                  <td className="price">175 pt</td>
                  <td className="price">75 pt</td>
                  <td className="price">35 pt</td>
                </tr>
              )}
              <tr>
                <td>2.0 スピード<br /><span className="model-name">seedance-2-fast</span></td>
                <td className="na">-</td>
                <td className="na">-</td>
                <td className="price">60 pt</td>
                <td className="price">30 pt</td>
              </tr>
              <tr>
                <td>2.0 ミニ<br /><span className="model-name">seedance-2-mini</span></td>
                <td className="na">-</td>
                <td className="na">-</td>
                <td className="price">35 pt</td>
                <td className="price">15 pt</td>
              </tr>
              <tr>
                <td>Google Gemini Omni</td>
                <td>スピード<br /><span className="model-name">gemini-omni-flash</span></td>
                <td className="price">150 pt</td>
                <td className="price">75 pt</td>
                <td className="price">50 pt</td>
                {/* Omni の最低解像度は 360p。専用列は設けず 480p 列に ※注記で掲載 (H3 と同じ扱い) */}
                <td className="price">15 pt<br /><span className="model-name">※360p</span></td>
              </tr>
              {showMinimaxH3 && (
                <tr>
                  <td>MiniMax</td>
                  <td>H3<br /><span className="model-name">minimax-h3</span></td>
                  {/* H3 の実解像度は 2K(=1440p)/768p。専用列は設けず近い列に ※注記で掲載 */}
                  <td className="na">-</td>
                  <td className="price">65 pt<br /><span className="model-name">※1440p</span></td>
                  <td className="price">40 pt<br /><span className="model-name">※768p</span></td>
                  <td className="na">-</td>
                </tr>
              )}
            </tbody>
          </table>
          <table className="subscription-pricing-table">
            <thead>
              <tr>
                <th>種別</th>
                <th>AIプロバイダ</th>
                <th>モード</th>
                <th>単価</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowSpan={3}>ナレーション生成</td>
                <td>ElevenLabs</td>
                <td>-<br /><span className="model-name">elevenlabs-tts</span></td>
                <td className="price">1 pt／本</td>
              </tr>
              <tr>
                <td>Google Gemini</td>
                <td>-<br /><span className="model-name">gemini-tts</span></td>
                <td className="price">1 pt／本</td>
              </tr>
              <tr>
                <td>Fish Audio</td>
                <td>-<br /><span className="model-name">fish-tts</span></td>
                <td className="price">1 pt／本</td>
              </tr>
              <tr>
                <td rowSpan={3}>BGM生成</td>
                <td>ElevenLabs</td>
                <td>-<br /><span className="model-name">elevenlabs-music</span></td>
                <td className="price">100 pt／分</td>
              </tr>
              <tr>
                <td rowSpan={2}>Google Gemini</td>
                <td>フル曲<br /><span className="model-name">lyria-pro-music</span></td>
                <td className="price">30 pt／曲</td>
              </tr>
              <tr>
                <td>30秒<br /><span className="model-name">lyria-clip-music</span></td>
                <td className="price">15 pt／曲</td>
              </tr>
              {/* シナリオ生成は各プロバイダ内で高額→安価の順 (ドロップダウンの並びと一致) */}
              <tr>
                <td rowSpan={10}>シナリオ生成</td>
                <td rowSpan={3}>Anthropic Claude</td>
                <td>高品質<br /><span className="model-name">claude-fable-5-1</span></td>
                <td className="price">30 pt／本</td>
              </tr>
              <tr>
                <td>標準<br /><span className="model-name">claude-opus-5-5</span></td>
                <td className="price">20 pt／本</td>
              </tr>
              <tr>
                <td>スピード<br /><span className="model-name">claude-sonnet-5-5</span></td>
                <td className="price">10 pt／本</td>
              </tr>
              <tr>
                <td rowSpan={3}>OpenAI GPT</td>
                <td>高品質<br /><span className="model-name">gpt-6-astra</span></td>
                <td className="price">30 pt／本</td>
              </tr>
              <tr>
                <td>標準<br /><span className="model-name">gpt-6.1-sol</span></td>
                <td className="price">10 pt／本</td>
              </tr>
              <tr>
                <td>スピード<br /><span className="model-name">gpt-6-luna</span></td>
                <td className="price">2 pt／本</td>
              </tr>
              <tr>
                <td rowSpan={4}>Google Gemini</td>
                <td>高品質<br /><span className="model-name">gemini-4-argon</span></td>
                <td className="price">15 pt／本</td>
              </tr>
              <tr>
                <td>高品質<br /><span className="model-name">gemini-3.1-pro</span></td>
                <td className="price">7 pt／本</td>
              </tr>
              <tr>
                <td>標準<br /><span className="model-name">gemini-3.8-flash</span></td>
                <td className="price">5 pt／本</td>
              </tr>
              <tr>
                <td>スピード<br /><span className="model-name">gemini-3.5-flash-lite</span></td>
                <td className="price">2 pt／本</td>
              </tr>
              <tr>
                <td rowSpan={2}>広告APIコール</td>
                <td>Amazon</td>
                <td>Amazon Selling Partner API</td>
                <td className="price">1 pt／回</td>
              </tr>
              <tr>
                <td>Google</td>
                <td>Google Ads API</td>
                <td className="price">1 pt／回</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="subscription-pricing-date">（2026年10月14日時点）</p>
        <p className="subscription-pricing-note">
          ※サブスクリプション契約の場合、広告APIコールは無料になります。
          <br />
          ※Google社などが提供するAIモデルの提供価格が変更された場合、消化ポイント単価を変更する場合があります。
          <br />
          ※新しいAIモデルが登場した場合、新しい料金体系が追加される可能性があります。既存のAIモデルが廃止された場合、そのモデルを利用するモードは提供終了となります。
        </p>
      </div>

      {/* フリープランへのダウングレード確認モーダル */}
      {showCancelModal && createPortal(
        <div className="modal-overlay subscription-modal" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">フリープランへのダウングレード</h3>
            <div className="modal-body">
              <p>
                現在のプラン: <strong>{subscription?.plan.display_name}</strong>
              </p>
              <p>
                変更予定日: <strong>{formatDate(subscription?.current_period_end || null)}</strong>
                （次回更新日）
              </p>
              <div className="modal-notice">
                <h4>ダウングレード後:</h4>
                <ul>
                  <li>フリープランに移行します</li>
                  <li>期限付きポイントは有効期限まで使用可能です</li>
                  <li>ストレージ上限を超過している場合、新規アップロードができなくなります</li>
                </ul>
              </div>
              <p className="modal-note">
                ※ ダウングレード予約後も{formatDate(subscription?.current_period_end || null)}
                まで現在のプランをご利用いただけます
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowCancelModal(false)}
              >
                キャンセル
              </button>
              <button
                className="btn-danger"
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? '処理中...' : 'ダウングレードを予約'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* アップグレード確認モーダル */}
      {showUpgradeModal && selectedPlan && createPortal(
        <div className="modal-overlay subscription-modal" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">プランをアップグレード</h3>
            <div className="modal-body">
              <p>
                現在のプラン: <strong>{subscription?.plan.display_name}</strong>
              </p>
              <p>
                変更先プラン:{' '}
                <strong>
                  {plans.find((p) => p.plan_code === selectedPlan)?.display_name}
                </strong>
              </p>
              <div className="modal-notice modal-notice-warning">
                <p>アップグレードは即時適用され、本日から日割り計算で差額が課金されます</p>
              </div>
              <p className="modal-note">
                ※ 変更後はすぐに新プランの機能をご利用いただけます
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowUpgradeModal(false);
                  setSelectedPlan(null);
                }}
              >
                キャンセル
              </button>
              <button
                className="btn-primary"
                onClick={handleUpgrade}
                disabled={createCheckout.isPending}
              >
                {createCheckout.isPending ? '処理中...' : 'アップグレードする'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ダウングレード確認モーダル */}
      {showDowngradeModal && selectedPlan && createPortal(
        <div className="modal-overlay subscription-modal" onClick={() => setShowDowngradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">プランを変更</h3>
            <div className="modal-body">
              <p>
                現在のプラン: <strong>{subscription?.plan.display_name}</strong>
              </p>
              <p>
                変更先プラン:{' '}
                <strong>
                  {plans.find((p) => p.plan_code === selectedPlan)?.display_name}
                </strong>
              </p>
              <div className="modal-notice modal-notice-warning">
                <p>ダウングレードは次回更新日から適用されます</p>
              </div>
              <p>
                変更適用日: <strong>{formatDate(subscription?.current_period_end || null)}</strong>
              </p>
              <p className="modal-note">
                ※ 予約後も{formatDate(subscription?.current_period_end || null)}
                まで現在のプランをご利用いただけます
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowDowngradeModal(false);
                  setSelectedPlan(null);
                }}
              >
                キャンセル
              </button>
              <button
                className="btn-primary"
                onClick={handleDowngrade}
                disabled={changePlanMutation.isPending}
              >
                {changePlanMutation.isPending ? '処理中...' : 'ダウングレードを予約'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
